import axios from 'axios'

import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('SandDollarsAPI')

// Get the API URL from environment variables
const SAND_DOLLARS_API_URL = process.env.REACT_APP_SAND_DOLLARS_BACKEND_URL

// Ensure API URL exists
if (!SAND_DOLLARS_API_URL) {
    logger.warn('REACT_APP_SAND_DOLLARS_BACKEND_URL is not defined')
}

// Create a base axios instance for the Sand Dollars API
const sandDollarsApi = axios.create({
    baseURL: SAND_DOLLARS_API_URL,
})

/**
 * Response type for the balance endpoint
 */
export interface ISandDollarsBalanceResponse {
    balance: string
    decimals: number
}

/**
 * Response type for the nonce endpoint
 */
export interface ISandDollarsNonceResponse {
    nonce: number
    wallet_address: string
}

/**
 * Fetch the Sand Dollars balance for a wallet address
 * @param address The wallet address to fetch the balance for
 * @returns The Sand Dollars balance response
 */
export const getSandDollarsBalance = async (address: string): Promise<ISandDollarsBalanceResponse> => {
    try {
        logger.info(`Fetching Sand Dollars balance for address: ${address}`)

        const response = await sandDollarsApi.get<ISandDollarsBalanceResponse>(`/v1/balance`, {
            params: {
                address,
            },
        })

        logger.info(`Received Sand Dollars balance response`, response.data)
        return response.data
    } catch (error) {
        logger.error('Error fetching Sand Dollars balance:', error)
        // Return a default balance of 0 with the standard decimals
        return {
            balance: '0',
            decimals: 9,
        }
    }
}

/**
 * Get a nonce for redeeming Sand Dollars
 * @param address The wallet address to get a nonce for
 * @returns The nonce response
 */
export const getSandDollarsRedeemNonce = async (address: string): Promise<ISandDollarsNonceResponse> => {
    try {
        logger.info(`Getting Sand Dollars redeem nonce for address: ${address}`)

        const response = await sandDollarsApi.get<ISandDollarsNonceResponse>(`/v1/redeem/get_nonce`, {
            params: {
                address,
            },
        })

        logger.info(`Received Sand Dollars nonce response`, response.data)
        return response.data
    } catch (error) {
        logger.error('Error getting Sand Dollars redeem nonce:', error)
        throw new Error('Failed to get redeem nonce for Sand Dollars')
    }
}

/**
 * Submit a redemption request for Sand Dollars
 * @param address The wallet address to redeem Sand Dollars for
 * @param nonce The nonce to use for the redemption
 * @param signature The signature of the redemption request
 * @param amount The amount of Sand Dollars to redeem
 * @returns Response data including transaction hash and chain ID
 */
interface RedeemResponse {
    success: boolean
    uuid: string
    tx_hash: `0x${string}`
    chain_id: string
}

export const redeemSandDollars = async (
    address: string,
    nonce: number,
    signature: string,
    amount: string
): Promise<RedeemResponse> => {
    try {
        logger.info(`Submitting Sand Dollars redemption for address: ${address}`)

        const response = await sandDollarsApi.post(`/v1/redeem/redeem`, {
            address,
            nonce,
            signature,
            amount,
        })

        logger.info(`Received Sand Dollars redemption response`, response.data)
        return response.data
    } catch (error) {
        logger.error('Error redeeming Sand Dollars:', error)
        throw new Error('Failed to redeem Sand Dollars')
    }
}
